import {
	FormModelFieldSubForm,
	isValidDefaultEnvironmentField,
	isValidMidSpanIkePhotoField,
	isValidSpanEnvironmentField,
	isValidSpanTypeField,
	isValidSpanVectorField,
	PoleFieldIds,
} from '@ikegps/core-shared';
import { m, type MithrilVNode } from '../../../../mithril';
import { SelectInputState } from '../../../../components/input/input.select';
import { IkeInputStateGroup } from '../../../../components/input/input.state.group';
import { MenuComponent } from '../../../../components/menu/menu';
import { type MenuItem, MenuItemComponent } from '../../../../components/menu/menu.item';
import { action, makeObservable, observable } from 'mobx';
import { T, Translate } from '../../../../i18n';
import { MobxState } from '../../../../state/mobx';
import { addStyles } from '../../../../components/theme';
import { SubformEditorDropdown } from './form.field.editor.subform.dropdown';
import { SubFormTypeEditorCSS, Style } from './form.field.editor.subform.style';
import type { FormFieldSelectList } from '@ikegps/core-api';

addStyles(Style);

export class SpanSubFormTypeEditor {
	private vectorFieldSelectState: SelectInputState;
	private typeFieldSelectState: SelectInputState;
	private environmentFieldSelectState: SelectInputState;
	private midSpanPhotoFieldSelectState: SelectInputState;

	private vectorMenu: MenuComponent;
	private typeMenu: MenuComponent;
	private environmentMenu: MenuComponent;
	private midSpanPhotoMenu: MenuComponent;

	private reactions: MobxState;
	private _allowCreateNewEnvironmentField: boolean = false;
	@observable private _environmentNoneSelected: boolean = false;

	constructor(
		private field: FormModelFieldSubForm,
		private stateGroup: IkeInputStateGroup
	) {
		makeObservable(this);
		this.reactions = new MobxState();

		const spanState = this.field.subFormSpanTypeState;

		// 1. Create all four select states with current options and values
		this.vectorFieldSelectState = this.createVectorFieldSelect(spanState?.vectorFieldId ?? null);
		this.typeFieldSelectState = this.createTypeFieldSelect(spanState?.typeFieldId ?? null);
		this.environmentFieldSelectState = this.createEnvironmentFieldSelect(spanState?.environmentFieldId ?? null);
		this.midSpanPhotoFieldSelectState = this.createMidSpanPhotoFieldSelect(spanState?.midSpanIkePhotoFieldId ?? null);

		// 2. Required states auto-select their first option; env/photo are not auto-selected
		this.vectorFieldSelectState.isRequired = true;
		this.typeFieldSelectState.isRequired = true;

		// 3. Create menus driven by the select states' options
		this.vectorMenu = this.createMenu(this.vectorFieldSelectState);
		this.typeMenu = this.createMenu(this.typeFieldSelectState);
		this.environmentMenu = this.createMenu(this.environmentFieldSelectState);
		this.midSpanPhotoMenu = this.createMenu(this.midSpanPhotoFieldSelectState);

		// 4. Register field-change reactions that update options when subform fields change
		this.reactions.reaction(
			() =>
				this.field.fields
					.filter(isValidSpanVectorField)
					.map((f) => f.displayName)
					.join(','),
			() => this.updateVectorFieldOptions()
		);
		this.reactions.reaction(
			() =>
				this.field.fields
					.filter(isValidSpanTypeField)
					.map((f) => f.displayName)
					.join(','),
			() => this.updateTypeFieldOptions()
		);
		this.reactions.reaction(
			() =>
				this.field.fields
					.filter(isValidSpanEnvironmentField)
					.map((f) => f.displayName)
					.join(','),
			() => this.updateEnvironmentFieldOptions()
		);
		this.reactions.reaction(
			() =>
				this.field.fields
					.filter(isValidMidSpanIkePhotoField)
					.map((f) => f.displayName)
					.join(','),
			() => this.updateMidSpanPhotoFieldOptions()
		);
		this.reactions.reaction(
			() =>
				this.field.fields
					.filter(isValidSpanEnvironmentField)
					.map((f) => {
						return JSON.stringify((f as unknown as FormFieldSelectList).options);
					})
					.join(','),
			() => {
				this.updateAllowCreateNewEnvironmentField();
			}
		);
		this.updateAllowCreateNewEnvironmentField();
		// 5. Register onValue handlers — fire when the user changes a dropdown
		this.vectorFieldSelectState.onValue((value) => {
			if (value) {
				this.field.Mutator.span.selectVectorField(value);
			}
		});
		this.typeFieldSelectState.onValue((value) => {
			if (value) {
				this.field.Mutator.span.selectSpanTypeField(value);
			}
		});
		this.environmentFieldSelectState.onValue((value) => {
			if (value) {
				this._environmentNoneSelected = false;
				this.environmentFieldSelectState.setError(null);
				this.field.Mutator.span.selectEnvironmentField(value);
			} else {
				this.field.Mutator.span.clearEnvironmentField();
			}
		});
		this.midSpanPhotoFieldSelectState.onValue((value) => {
			if (value) {
				this.midSpanPhotoFieldSelectState.setError(null);
				this.field.Mutator.span.selectMidSpanIkePhotoField(value);
			}
		});
	}

	private createMenu(state: SelectInputState): MenuComponent {
		return new MenuComponent({
			items: (): MenuItem[] =>
				state.options.map((opt) => ({
					view: MenuItemComponent.view([opt.key]),
					action: () => state.setValue(opt.value),
				})),
		});
	}

	private createVectorFieldSelect(currentValue: string | null): SelectInputState {
		const options = this.field.fields
			.filter(isValidSpanVectorField)
			.map((field) => ({ key: field.displayName, value: field.id }));

		const state = new SelectInputState(Translate(T.SPAN_VECTOR_FIELD), options);
		state.setValue(currentValue);
		this.stateGroup.add(state);

		return state;
	}

	private createTypeFieldSelect(currentValue: string | null): SelectInputState {
		const options = this.field.fields
			.filter(isValidSpanTypeField)
			.map((field) => ({ key: field.displayName, value: field.id }));

		const state = new SelectInputState(Translate(T.SPAN_TYPE_FIELD), options);
		state.setValue(currentValue);
		this.stateGroup.add(state);

		return state;
	}

	private createEnvironmentFieldSelect(currentValue: string | null): SelectInputState {
		const options = this.field.fields
			.filter(isValidSpanEnvironmentField)
			.map((field) => ({ key: field.displayName, value: field.id }));

		const state = new SelectInputState(Translate(T.SPAN_ENVIRONMENT_FIELD), options);

		state.setValue(currentValue);

		this.stateGroup.add(state);

		return state;
	}

	private createMidSpanPhotoFieldSelect(currentValue: string | null): SelectInputState {
		const options = this.field.fields
			.filter(isValidMidSpanIkePhotoField)
			.map((field) => ({ key: field.displayName, value: field.id }));

		const state = new SelectInputState(Translate(T.SPAN_MID_SPAN_IKE_PHOTO_FIELD), options);

		if (currentValue) {
			state.setValue(currentValue);
		} else {
			state.setError(Translate(T.INPUT_REQUIRED));
			state.setTouched();
		}

		this.stateGroup.add(state);

		return state;
	}

	@action private updateVectorFieldOptions() {
		if (!this.vectorFieldSelectState) return;

		const options = this.field.fields
			.filter(isValidSpanVectorField)
			.map((field) => ({ key: field.displayName, value: field.id }));

		this.vectorFieldSelectState.setOptions(options);

		if (!options.find((opt) => opt.value === this.vectorFieldSelectState.value)) {
			this.vectorFieldSelectState.setValue(null);
		}
	}

	@action private updateTypeFieldOptions() {
		if (!this.typeFieldSelectState) return;

		const options = this.field.fields
			.filter(isValidSpanTypeField)
			.map((field) => ({ key: field.displayName, value: field.id }));

		this.typeFieldSelectState.setOptions(options);

		if (!options.find((opt) => opt.value === this.typeFieldSelectState.value)) {
			this.typeFieldSelectState.setValue(null);
		}
	}

	@action private updateEnvironmentFieldOptions() {
		if (!this.environmentFieldSelectState) return;

		const options = this.field.fields
			.filter(isValidSpanEnvironmentField)
			.map((field) => ({ key: field.displayName, value: field.id }));

		this.environmentFieldSelectState.setOptions(options);

		if (!options.find((opt) => opt.value === this.environmentFieldSelectState.value)) {
			this.environmentFieldSelectState.setValue(null);
		}
	}

	@action private updateMidSpanPhotoFieldOptions() {
		if (!this.midSpanPhotoFieldSelectState) return;

		const options = this.field.fields
			.filter(isValidMidSpanIkePhotoField)
			.map((field) => ({ key: field.displayName, value: field.id }));

		this.midSpanPhotoFieldSelectState.setOptions(options);

		if (!options.find((opt) => opt.value === this.midSpanPhotoFieldSelectState.value)) {
			this.midSpanPhotoFieldSelectState.setValue(null);
		}
	}

	allowCreateNewEnvironmentField(): boolean {
		return this._allowCreateNewEnvironmentField;
	}

	@action private updateAllowCreateNewEnvironmentField() {
		const fieldWithEnvFieldId = this.field.fields.filter(
			(field) => field.id === PoleFieldIds.SPAN.ENVIRONMENT && isValidSpanEnvironmentField(field)
		);
		const validDefaultEnvAvailable = this.field.fields.find(isValidDefaultEnvironmentField);
		this._allowCreateNewEnvironmentField = fieldWithEnvFieldId.length === 0 && !validDefaultEnvAvailable;
	}

	hasNoValidMidSpanPhotoOptions(): boolean {
		return this.field.fields.filter(isValidMidSpanIkePhotoField).length === 0;
	}

	@action private selectEnvironmentNone() {
		this._environmentNoneSelected = true;
		this.environmentFieldSelectState.setValue(null);
	}

	@action createEnvironmentField() {
		const typeIndex = this.field.fields.findIndex(isValidSpanTypeField);
		const insertIndex = typeIndex >= 0 ? typeIndex : 2;
		const field = this.field.Mutator.span.addEnvironmentField(insertIndex);

		const options = this.field.fields
			.filter(isValidSpanEnvironmentField)
			.map((f) => ({ key: f.displayName, value: f.id }));
		this.environmentFieldSelectState.setOptions(options);
		this.environmentFieldSelectState.setValue(field.id);
		this.environmentFieldSelectState.setError(null);
		this.field.Mutator.span.selectEnvironmentField(field.id);
	}

	@action createMidSpanPhotoField() {
		const field = this.field.Mutator.span.addMidSpanPhotoField();

		const options = this.field.fields
			.filter(isValidMidSpanIkePhotoField)
			.map((f) => ({ key: f.displayName, value: f.id }));
		this.midSpanPhotoFieldSelectState.setOptions(options);
		this.midSpanPhotoFieldSelectState.setValue(field.id);
		this.midSpanPhotoFieldSelectState.setError(null);
	}

	view(): MithrilVNode {
		const selectedText = (state: SelectInputState): string => {
			const opt = state.options.find((o) => o.value === state.value);
			return opt?.key ?? Translate(T.SELECT_OPTION);
		};

		const stateError = (state: SelectInputState): string | null =>
			state.touched && !state.isValid && state.hasError() ? Translate(state.error) : null;

		return m('div', { className: SubFormTypeEditorCSS.wrapper }, [
			m('hr'),
			m('div', { className: SubFormTypeEditorCSS.grid }, [
				SubformEditorDropdown.view({
					title: Translate(T.SPAN_VECTOR_FIELD),
					selectedText: selectedText(this.vectorFieldSelectState),
					menu: this.vectorMenu,
				}),
				SubformEditorDropdown.view({
					title: Translate(T.SPAN_TYPE_FIELD),
					selectedText: selectedText(this.typeFieldSelectState),
					menu: this.typeMenu,
				}),
				SubformEditorDropdown.view({
					title: Translate(T.SPAN_ENVIRONMENT_FIELD),
					selectedText: this._environmentNoneSelected
						? Translate(T.NONE)
						: selectedText(this.environmentFieldSelectState),
					menu: this.environmentMenu,
					allowNone: true,
					noneSelected: this._environmentNoneSelected,
					onNoneSelect: () => this.selectEnvironmentNone(),
					actionBtn:
						this.allowCreateNewEnvironmentField() || this._environmentNoneSelected
							? { action: () => this.createEnvironmentField(), btnText: Translate(T.FORM_FIELD_CREATE) }
							: undefined,
				}),
				SubformEditorDropdown.view({
					title: Translate(T.SPAN_MID_SPAN_IKE_PHOTO_FIELD),
					selectedText: selectedText(this.midSpanPhotoFieldSelectState),
					menu: this.midSpanPhotoMenu,
					error: stateError(this.midSpanPhotoFieldSelectState),
					actionBtn: this.hasNoValidMidSpanPhotoOptions()
						? { action: () => this.createMidSpanPhotoField(), btnText: Translate(T.FORM_FIELD_CREATE) }
						: undefined,
				}),
			]),
		]);
	}

	destroy() {
		this.reactions.destroy();

		// Remove select states from the parent state group
		this.stateGroup.remove(this.vectorFieldSelectState);
		this.stateGroup.remove(this.typeFieldSelectState);
		this.stateGroup.remove(this.environmentFieldSelectState);
		this.stateGroup.remove(this.midSpanPhotoFieldSelectState);
	}
}
